/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FileUploadService } from '../file-upload-service';
import type { UploadResult } from '../../types/file-upload';
import { FileUploadApi } from '../../apis/file-upload-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { FileStorageService } from '../file-storage-service';

/**
 * 基于 FileUploadApi 的文件上传服务实现
 * 使用真实的HTTP API进行文件上传
 */
@injectable()
export class FileUploadServiceImpl implements FileUploadService {
    constructor(
        @inject(FileUploadApi)
        private readonly _fileUploadApi: FileUploadApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(FileStorageService)
        private readonly _fileStorageService: FileStorageService,
    ) {}

    /**
     * 上传文件到服务器
     * @param params 上传参数
     * @param params.file 要上传的文件
     * @param params.contentType 文件内容类型
     * @param onProgress 进度回调函数
     * @returns 上传结果
     */
    async upload(
        params: { file: File; contentType: 'DISCLOSURE' | 'CLAIM' },
        onProgress?: (progress: number) => void,
    ): Promise<UploadResult> {
        const { file, contentType } = params;

        // 获取当前任务ID
        const taskId = this._currentTaskService.taskId;
        if (!taskId || taskId.trim() === '') {
            throw new Error('当前没有可用的任务ID');
        }

        try {
            // 模拟进度回调（因为当前API不支持进度跟踪）
            await this._simulateProgress(onProgress);

            // 调用API上传文件
            const response = await this._fileUploadApi.uploadFile({
                taskId,
                file,
                contentType,
            });

            // 检查上传是否成功
            if (!response.success) {
                throw new Error('文件上传失败');
            }

            // 设置最终进度为100%
            if (onProgress) {
                onProgress(100);
            }

            // 构造上传结果
            const result = this._createUploadResult(file, taskId);

            // 将文件存储到文件存储服务中，供后续识别使用
            this._fileStorageService.storeFile(result.fileId, file);

            return result;
        } catch (error) {
            // 更新进度为失败状态
            if (onProgress) {
                onProgress(0);
            }
            throw error;
        }
    }

    /**
     * 模拟上传进度
     * 注意：当前API不支持真实的进度跟踪，这里提供基本的进度反馈
     */
    private async _simulateProgress(onProgress?: (progress: number) => void): Promise<void> {
        if (!onProgress) return;

        // 立即显示开始上传
        onProgress(0);

        // 使用 Promise 来处理异步进度更新，确保测试可以等待
        await new Promise<void>((resolve) => {
            let progress = 0;
            const updateProgress = () => {
                if (progress === 0) {
                    progress = 30;
                    onProgress(30);
                    setTimeout(updateProgress, 50);
                } else if (progress === 30) {
                    progress = 60;
                    onProgress(60);
                    setTimeout(updateProgress, 50);
                } else if (progress === 60) {
                    progress = 90;
                    onProgress(90);
                    setTimeout(() => resolve(), 50);
                }
            };
            setTimeout(updateProgress, 50);
        });
    }

    /**
     * 基于文件信息创建上传结果
     * 注意：由于当前API只返回success状态，我们需要生成合理的默认值
     */
    private _createUploadResult(file: File, taskId: string): UploadResult {
        const timestamp = Date.now();
        const sanitizedFileName = this._sanitizeFileName(file.name);

        return {
            fileId: `${taskId}_${timestamp}_${sanitizedFileName}`,
            fileUrl: `/ai-specification/task/${taskId}/files/${sanitizedFileName}`,
            uploadTime: new Date(),
        };
    }

    /**
     * 清理文件名，移除特殊字符
     */
    private _sanitizeFileName(fileName: string): string {
        return fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    }
}
